import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { getLocaleProfile } from '../../../shared/config/localeProfile';
import { BRAND } from '../../../shared/config/brand';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.my',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: BRAND.logoUrl,
    primaryColor: BRAND.colors.primary,
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.my/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'Lot 12, Jalan Sultan Ismail\nKuala Lumpur 50250\nMalaysia',
    ccEmail: 'receipts@bilabilamart.my',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Ahmed Lee',
    email: 'ahmed@bilabilamart.my',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Aishath Tan',
    email: 'aishath@bilabilamart.my',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ibrahim Rahman',
    email: 'ibrahim@bilabilamart.my',
    role: 'Operations',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Malaysian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD-',
    startingNumber: 1000
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MYR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: 'BML - 773000000001',
    frequency: 'Weekly',
    minimumAmount: 1000
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.my',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Ahmed Lee',
      email: 'ahmed@bilabilamart.my',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Aishath Tan',
      email: 'aishath@bilabilamart.my',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ibrahim Rahman',
      email: 'ibrahim@bilabilamart.my',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MYR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: CreditCardIcon,
    category: 'account'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-10423" data-mp="2tlz0p2org7li">
      <button className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10424" data-mp="2oo5y0jmo1vpz">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-10425" data-mp="3hcus3ypb10s8">
      <div data-id="element-10426" data-mp="3cea02mk7hnev">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10427" data-mp="3cejhhlr6qm8y">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10428" data-mp="37enki6gwnv2w">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10429" data-mp="1w01qrtog5al6">
        <div className="flex items-start gap-3" data-id="element-10430" data-mp="1vxxbkfl3kp5l">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10431" data-mp="1ry6pgp6imx7e" />
          <div className="text-sm text-gray-700" data-id="element-10432" data-mp="3r3g71kbvm458">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10433" data-mp="3r1n003xcphwp">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-10434" data-mp="3w3kn598vgwux">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10435" data-mp="emb8bl1039q0">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10436" data-mp="imbn56s0hmxl">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10437" data-mp="4w85kt42uheh">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10438" data-mp="9v30gtfbjwey" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10439" data-mp="xk3zjq29hv7k">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-10440" data-mp="xj2rlugjlou2">
            <div className="flex items-center gap-2" data-id="element-10441" data-mp="12dgpc3iog81w">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-10442" data-mp="1bp4cjn0befxi" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-10443" data-mp="1lf7cy89rmw5v">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-10444" data-mp="sqs4y5p1xe41" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-10445" data-mp="orj6womzau2o">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-10446" data-mp="12prd7n5wn7qi" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-10447" data-mp="1yrgcnw6ozfn">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-10448" data-mp="3t63zh8gmnmy7" />
          </FormField>

          <FormField label={getLocaleProfile().taxIdLabel} data-id="element-10449" data-mp="bz1uqbtygcwe">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-10450" data-mp="bxgxgeg9afuu" />
          </FormField>

          <FormField label="Business Email" required data-id="element-10451" data-mp="1byc5ynl7fmoh">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-10452" data-mp="1c0yb6gkmhq3j" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-10453" data-mp="1zo6h90pvl36m">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-10454" data-mp="1zqsmgtpan6lo" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10455" data-mp="9m7jaud8o219" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-10456" data-mp="nct10fqyi5xk">
      <div data-id="element-10457" data-mp="nduf6t39r8ji">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10458" data-mp="ndm3v5msobaf">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-10459" data-mp="ijq90c1tv4jh">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10460" data-mp="s9szo5lh86en">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10461" data-mp="16b5dobh2txxw">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10462" data-mp="1689ekjtht37v">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-10463" data-mp="119fz4lunb252" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-10464" data-mp="3335j9gazl6ah">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-10465" data-mp="33476sk96vl93">
            <div className="flex items-center gap-4" data-id="element-10466" data-mp="1njy4d5xndifp">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-10467" data-mp="19st2ryapyg2q">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-10468" data-mp="19qqdxhlayszl" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-10469" data-mp="14t6s9wm9zdxm">
                <UploadIcon size={16} className="mr-2" data-id="element-10470" data-mp="qv0bxudddwlp" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-10471" data-mp="2l7q6mebkb9u5">
            <div className="flex items-center gap-3" data-id="element-10472" data-mp="2p80b1mjphs6w">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10473" data-mp="20inihyghe15v" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-10474" data-mp="1hjjfb6bmf1m4" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-10475" data-mp="xw2a5iplzl1a">
                Current: BML Red
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10476" data-mp="35hu6oyyeocpn" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-10477" data-mp="35jwwpizk65ud">
      <div data-id="element-10478" data-mp="39imhhwmxepo1">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10479" data-mp="39ie29y3bz89o">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10480" data-mp="3ejaa2mqszekd">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10481" data-mp="2lu381ds546ud">
        <div className="flex items-start gap-3" data-id="element-10482" data-mp="2qso18t8z5q9b">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10483" data-mp="2qtfye8i7zw1p" />
          <div className="text-sm text-gray-700" data-id="element-10484" data-mp="2vrhcsg4pb76u">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10485" data-mp="2vpo7b45tqt0f">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-10486" data-mp="30kagr1scibhv">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10487" data-mp="1zznvc2m4vh2j">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10488" data-mp="1vyu9rfw5yect">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10489" data-mp="29rtkz5owxmek">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-10490" data-mp="24sgzopdowne3" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10491" data-mp="1h1utl3n8xd6q">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-10492" data-mp="1h2wcfuai73z0">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-10493" data-mp="1r1bw5c77bayh" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-10494" data-mp="o86lf1j9ekrd">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-10495" data-mp="a9qi7kklvcdz" />
          </FormField>

          <div className="pt-2" data-id="element-10496" data-mp="y5kswbn7fz7j">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10497" data-mp="y4t8zy2yzpx3">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-10498" data-mp="1lvoygojye916" />
              <div data-id="element-10499" data-mp="3d09y3n5sgjax">
                <p className="text-sm font-medium text-gray-900" data-id="element-10500" data-mp="3cxnv80tuerz1">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-10501" data-mp="9jjlgpfpb809">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-10502" data-mp="3vp6txvgu3jv9" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-10503" data-mp="3rpfxbb4mm9it">
      <div data-id="element-10504" data-mp="3ro4upemx37ta">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10505" data-mp="3romxku434ka6">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10506" data-mp="2k98en9v2c1ll">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10507" data-mp="vt9xg5ckw2ns">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10508" data-mp="zsio31cbnkf4">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10509" data-mp="14snaf7zwv9d9">
            <MailIcon size={18} className="text-gray-600" data-id="element-10510" data-mp="19j3mano32mxi" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10511" data-mp="1eipzix3ww36b">
          <div className="pb-2" data-id="element-10512" data-mp="1ehomwo78z8mh">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-10513" data-mp="1jh9nf322fp1b">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-10514" data-mp="1jfgfzljmp0s9" />
              <div data-id="element-10515" data-mp="9tufoja5luq7">
                <p className="text-sm font-medium text-gray-900" data-id="element-10516" data-mp="9um2b4lrwi4c">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-10517" data-mp="erckbxh51nse">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-10518" data-mp="oqla0e4z0g8y">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-10519" data-mp="3s7k5dkdv3r2q" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-10520" data-mp="39f7v8bgyvli1">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-10521" data-mp="1mk3090aufiko" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-10522" data-mp="12vc57as4u04a">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-10523" data-mp="tvxc03kkzbg3" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10524" data-mp="f6vqndhga3rd" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-10525" data-mp="1b1ttstyb4um">
      <div data-id="element-10526" data-mp="5b2h82z12qdt">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10527" data-mp="5asmk231fv1s">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10528" data-mp="t04wx5a3q95o">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10529" data-mp="k8bc36njicaa">
        <div className="flex items-start gap-3" data-id="element-10530" data-mp="k830di35ld0o">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10531" data-mp="o94z7xt5rn24" />
          <div className="text-sm text-gray-700" data-id="element-10532" data-mp="1bypk4m8vgue1">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10533" data-mp="1gq7m9dmmt10l">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-10534" data-mp="31g4lfuz9rsi7">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10535" data-mp="367cz537mu016">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10536" data-mp="369fum2rk0lda">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10537" data-mp="2iijzavnw381a">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-10538" data-mp="2djqt7oaxmjb5" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10539" data-mp="2shj6e9t0avm6">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10540" data-mp="7rk4agyn7grh">
                  <div className="flex items-center gap-3" data-id="element-10541" data-mp="28grejzgkc6us">
                    <Icon size={20} className="text-gray-600" data-id="element-10542" data-mp="2df17e3etevd8" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10543" data-mp="2hfl4x83aw0zw">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10544" data-mp="2mg94wj9ibs69">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10545" data-mp="2rczlr8t4yrsq" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10546" data-mp="3k874qm00f32h"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10547" data-mp="6tard9zuqroj">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10548" data-mp="6vdt2rada993">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10549" data-mp="219je09pvn4b">
            <WalletIcon size={18} className="text-gray-600" data-id="element-10550" data-mp="3u5bvkz4svmpm" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10551" data-mp="uk6g1y90h6rk">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10552" data-mp="2oz3u78gjr490">
                  <div className="flex items-center gap-3" data-id="element-10553" data-mp="2a84pi8iu0zup">
                    <Icon size={20} className="text-gray-600" data-id="element-10554" data-mp="2f88yldheusaw" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10555" data-mp="219sordo94loj">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10556" data-mp="269xabi40o6lm">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10557" data-mp="1rcwep3ipfrhv" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10558" data-mp="1k06vzvjo2zux"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10559" data-mp="22qonjwsrjpep">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10560" data-mp="22oloyodjonyd">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10561" data-mp="dcjlaq3kzrk5">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-10562" data-mp="8dp1bktvnwyg" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10563" data-mp="3iocl2g7ocqqf">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10564" data-mp="3sh1k2bm1gc4v">
                  <div className="flex items-center gap-3" data-id="element-10565" data-mp="2zruo9kdyy0nb">
                    <Icon size={20} className="text-gray-600" data-id="element-10566" data-mp="34q5uzm8yvv1b" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-10567" data-mp="39o7f8ai4q0ed">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-10568" data-mp="3eovcvf0v5fhk">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-10569" data-mp="ox6xs0zpvcw5" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10570" data-mp="1qpj7pa5oh3sm"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-10571" data-mp="3b5d0i78h8t5h">
      <div className="flex items-center justify-between" data-id="element-10572" data-mp="3b6eeokkshvrf">
        <div data-id="element-10573" data-mp="59wa241mrvxm">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10574" data-mp="59mfe35n50ll">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10575" data-mp="bkxvmnv59c8">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-10576" data-mp="ez63poc4hmnu">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-10577" data-mp="a0mbx7aulgnk">
            <ShieldIcon size={16} className="mr-2" data-id="element-10578" data-mp="3dicy7b2e611a" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md flex items-center hover:bg-[#EB4A42] transition-colors" data-id="element-10579" data-mp="3ri5l0vasrx3z">
            <PlusIcon size={16} className="mr-2" data-id="element-10580" data-mp="16p25rfkzqc6h" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10581" data-mp="1a7rg1zpd4x6r">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10582" data-mp="1f8f88opbvk2k">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-10583" data-mp="10amthyrluzpf">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10584" data-mp="1596jca9bkvn2">
            <FormField label="Email Address" required data-id="element-10585" data-mp="159yejjz08rct">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-10586" data-mp="1sz0sagqiq021" />
            </FormField>
            <FormField label="Role" required data-id="element-10587" data-mp="2byerpe70o2zx">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-10588" data-mp="2bwlibr50r4o8">
                <option value="Owner" data-id="element-10589" data-mp="2lmts4ucfavff">Owner</option>
                <option value="Merchant Management" data-id="element-10590" data-mp="2ljxwjco1siu5">Merchant Management</option>
                <option value="Account Management" data-id="element-10591" data-mp="2hicc9696p29x">Account Management</option>
                <option value="Sales" data-id="element-10592" data-mp="2ckas0i00uwwv">Sales</option>
                <option value="Finance" data-id="element-10593" data-mp="27lzk4ctaewha">Finance</option>
                <option value="Operations" data-id="element-10594" data-mp="27nkbjtkb1zax">Operations</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-10595" data-mp="1xwpncjni0pmu">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-10596" data-mp="1tvvyno28f6sw">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-10597" data-mp="1oxcent778536">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-10598" data-mp="4bmegs8gyvxf">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-10599" data-mp="2jykohrix1eae">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-10600" data-mp="18c2wxpofari5">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-10601" data-mp="2w8afzi66tms5">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10602" data-mp="2wb6cr36au5f0">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-10603" data-mp="16uatdb5jxvov">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-10604" data-mp="1y11bt336xv41">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-10605" data-mp="220ihnxmj4u9q">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10606" data-mp="270eixp3ue7ll">
            <UsersIcon size={18} className="text-gray-600" data-id="element-10607" data-mp="2bz7zxric8gqi" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-10608" data-mp="2gz42rnfau24h">
          <table className="w-full" data-id="element-10609" data-mp="2gxj7twp2oh63">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10610" data-mp="2gwhwdr459snu">
              <tr data-id="element-10611" data-mp="2kycw4r6ffxzi">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10612" data-mp="2kxte7ags19lh">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10613" data-mp="2upz7cnirlx29">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10614" data-mp="2zmzaoqo8clpo">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10615" data-mp="3pxirov25lgh2">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10616" data-mp="2tvxx267lekl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10617" data-mp="36al60slfwwcd">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-10618" data-mp="329j587bvkc8a">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-10619" data-mp="327g4otd3j0pb">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10620" data-mp="3b9fp7vnbkmx0">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10621" data-mp="2nkd3ocpke9x8">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-10622" data-mp="2nia9rhlajwn8">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-10623" data-mp="2im1wk9qnmdip">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-10624" data-mp="u1e81tubqrkr">
                    <div className="flex items-center justify-end gap-2" data-id="element-10625" data-mp="tzjnwhqvn3hq">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-10626" data-mp="1lstr5dwo9w68">Owner</span> : <div className="relative inline-block" data-id="element-10627" data-mp="17taxtr8ispdz">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-10628" data-mp="17ssq9ygeqoqr">
                            <MoreHorizontalIcon size={18} data-id="element-10629" data-mp="6pcl23ntwoz5" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-10630" data-mp="3ttaaiureuxls" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-10631" data-mp="3k2fn3n2fhryr">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-10632" data-mp="2rhsf2wgjecks">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-10633" data-mp="3o7ry5kubcthm">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-10634" data-mp="1gn3yy9ndq1t4">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-10635" data-mp="22tn814i638j1">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10636" data-mp="f2ffwn8m2giz">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-10637" data-mp="3nhfwol2ei2re">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10638" data-mp="3ij4m284a7o84">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10639" data-mp="1mfzsaltp8vux">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-10640" data-mp="1re1al4jawr5c">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10641" data-mp="207o38ls7peab">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10642" data-mp="18ht6w55xsoc5" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-10643" data-mp="2nya33ps2bs98">
              <FormField label="Name" data-id="element-10644" data-mp="2nxiitb3x1gy9">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-10645" data-mp="2swlbrx6mfwpv" />
              </FormField>
              <FormField label="Email" data-id="element-10646" data-mp="2p7khfwdb1dgb">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-10647" data-mp="2kaka7i4py4nm" />
              </FormField>
              <FormField label="Role" data-id="element-10648" data-mp="4imrycflut5g">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-10649" data-mp="4jo12bd295kj">
                  <option value="Merchant Management" data-id="element-10650" data-mp="iyuvxhy5ogz9">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-10651" data-mp="92rqa243i07k">Account Management</option>
                  <option value="Sales" data-id="element-10652" data-mp="90niva6zmj2j">Sales</option>
                  <option value="Finance" data-id="element-10653" data-mp="3r9y0zf7p6dxt">Finance</option>
                  <option value="Operations" data-id="element-10654" data-mp="220bf1in41r">Operations</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-10655" data-mp="1pib97bfzef0a">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-10656" data-mp="1uf1jfi53ugds">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[#E2231A] text-white rounded-md hover:bg-[#EB4A42]" data-id="element-10657" data-mp="2dd4qrpfreekz">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-10658" data-mp="6vw4t0wbb7lu">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-10659" data-mp="1xb5d3ovwryg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-10660" data-mp="3oat7isl3xelm">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-10661" data-mp="3k9hbo5bn3x4z">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-10662" data-mp="3f8tknjnevgar">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-10663" data-mp="35bxcjf1fcw6q">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-10664" data-mp="13kas5wc1fzj8" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-10665" data-mp="1g13gysszah1t">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-10666" data-mp="1ktd1ftbbqowp">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-10667" data-mp="1pukd43gl888n">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-10668" data-mp="x53ir09eqh9s">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-10669" data-mp="122lvl1qjpgn2">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-10670" data-mp="2dikahm8emww7">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-10671" data-mp="2dhsdq832mt4c">
                  <thead className="bg-gray-50" data-id="element-10672" data-mp="2igc1me186ezc">
                    <tr data-id="element-10673" data-mp="2ij7zk2d2p3ns">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-10674" data-mp="1sszo6473ozxd">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-10675" data-mp="22p2yhxc3w4ve">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-10676" data-mp="1junyq5hz086i">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10677" data-mp="1f24l58ja9d1p"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-10678" data-mp="md70wzvih66b">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-10679" data-mp="mce2qnambozs">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10680" data-mp="32sao6hezni11">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10681" data-mp="2t0o929zj4yt0">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10682" data-mp="3lqn1mfcxne2f">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-10683" data-mp="3brxh0etnjfih">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-10684" data-mp="20afpcdpci6cb">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-10685" data-mp="2br3s5s4uhoyn">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10686" data-mp="26un38vptn6ki">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-10687" data-mp="2ll2ogai8meio">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-10688" data-mp="1e6hi47uuvfk5">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10689" data-mp="1a67bqu35in4r" /> : <span className="text-gray-300" data-id="element-10690" data-mp="3ur32aoz09n9x">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10691" data-mp="8cjyfm7npqhp">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10692" data-mp="dabp6cwkf6kb" /> : <span className="text-gray-300" data-id="element-10693" data-mp="2j4cr8cdvwwxi">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10694" data-mp="2o47i4fxntmik">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10695" data-mp="3rgfz4kghb682" /> : <span className="text-gray-300" data-id="element-10696" data-mp="3vevvheyg9qy5">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10697" data-mp="98qikp0i3uk5">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10698" data-mp="n0dg0s66j94i" /> : <span className="text-gray-300" data-id="element-10699" data-mp="wvyjyvut6jgw">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-10700" data-mp="1bmbey2e2ft4n">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-10701" data-mp="1gi0h8dhgzo4n" /> : <span className="text-gray-300" data-id="element-10702" data-mp="sth85vqlj7bi">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-10703" data-mp="9w6a2egpajxn">
                <p className="font-medium mb-1" data-id="element-10704" data-mp="dxrinnm7cc21">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-10705" data-mp="dz2mfnfndjt5">
                  <span data-id="element-10706" data-mp="ubfwlrdernmc">
                    <strong data-id="element-10707" data-mp="uaoad75pb28q">R</strong> = Read
                  </span>
                  <span data-id="element-10708" data-mp="pez7atuh337o">
                    <strong data-id="element-10709" data-mp="pefrpjsaoqwt">W</strong> = Write
                  </span>
                  <span data-id="element-10710" data-mp="kdjicqp6cck0">
                    <strong data-id="element-10711" data-mp="kcqdjv9rzx4i">M</strong> = Modify
                  </span>
                  <span data-id="element-10712" data-mp="fegidko1wwiw">
                    <strong data-id="element-10713" data-mp="ff8d6t9tqq84">I</strong> = Insert
                  </span>
                  <span data-id="element-10714" data-mp="bey4h8undlpk">
                    <strong data-id="element-10715" data-mp="bf7nudl6suma">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-10716" data-mp="1o399ezfbbrz">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-10717" data-mp="1lz30qe1y0oj">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-10718" data-mp="1zavzhjaxgjwi">
      <div data-id="element-10719" data-mp="1zdqm9hheygt3">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10720" data-mp="23d93o3a109r6">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10721" data-mp="28cc75jacy7x1">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10722" data-mp="2d9cedxiy1gpq">
        <div className="flex items-start gap-3" data-id="element-10723" data-mp="1fp9e36ogape6">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10724" data-mp="1kmhyldx2n5il" />
          <div className="text-sm text-gray-700" data-id="element-10725" data-mp="1kkeyg127fw05">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10726" data-mp="1pkt7nmfjlbzr">Legal Compliance</p>
            <p className="text-xs" data-id="element-10727" data-mp="1ud2ugtld1vxt">
              These terms are displayed to customers during checkout. Ensure
              they comply with Malaysian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10728" data-mp="11tx7o20xnahf">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10729" data-mp="16rvja59mmpbl">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10730" data-mp="1bqghjz5biyxe">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-10731" data-mp="1go8jllzog1fb" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-10732" data-mp="1locp6gz1rbqr">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-10733" data-mp="1lli6atvolyzg">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-10734" data-mp="1unzql2rs0hjt" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10735" data-mp="1rnrfld0mtk4">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10736" data-mp="3tv7y0wzjr2ku">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10737" data-mp="3txau9yralry0">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10738" data-mp="3owdettxo3c07" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-10739" data-mp="3jz4r7dtr2frk">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-10740" data-mp="lfwj7uancxj3">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-10741" data-mp="ggtlyw0wrvlo" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10742" data-mp="2tnjlbeh9ua0a" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-10743" data-mp="2xp4rkgzavq1i">
      <div data-id="element-10744" data-mp="2xo3giciab3js">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10745" data-mp="2xnto6i9rokax">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10746" data-mp="2jv410aii59c8">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10747" data-mp="29xzgsg87vny2">
        <div className="flex items-start gap-3" data-id="element-10748" data-mp="2eyn34oli3gly">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10749" data-mp="2ezf1262klqfe" />
          <div className="text-sm text-gray-700" data-id="element-10750" data-mp="20znp0avm6k41">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10751" data-mp="25xfjncnn8kbx">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-10752" data-mp="3vcn2nygfp48o">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10753" data-mp="9cr2omyig7fh">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10754" data-mp="n3ck078bg9b9">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10755" data-mp="ib2psf064n36">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-10756" data-mp="x08m9v7mfz7d" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-10757" data-mp="s274ddlxm5xh">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-10758" data-mp="s0vyn88xeo3q" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-10759" data-mp="1203ga7mp14wn">
      <div data-id="element-10760" data-mp="37sjvz4ruqin2">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10761" data-mp="37sa2363orrc3">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10762" data-mp="33rggijdpuomd">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10763" data-mp="3a76xq3ca9ew4">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10764" data-mp="3a9b730syb63s">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10765" data-mp="3ea3fjru016z0">
            <BellIcon size={18} className="text-gray-600" data-id="element-10766" data-mp="3j6tuucxzbyjd" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10767" data-mp="2mjgxcs3xhxnr">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10768" data-mp="2qipp5rferlgo">
            <div data-id="element-10769" data-mp="2vi2b89yggoi7">
              <p className="text-sm font-medium text-gray-900" data-id="element-10770" data-mp="2vhitoucpw24p">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10771" data-mp="30aby1h49x8g9">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10772" data-mp="27sq4dgqlosj2">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-10773" data-mp="27qdduf6s19ue" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10774" data-mp="1mbii6mjf95gm"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10775" data-mp="17igrddnods2v">
            <div data-id="element-10776" data-mp="12jlvpb4m091c">
              <p className="text-sm font-medium text-gray-900" data-id="element-10777" data-mp="yjlks0gpyfz1">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10778" data-mp="tkid089ctvnd">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10779" data-mp="jvuwkrcj3hsg">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-10780" data-mp="nzj5bdcwcjfa" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10781" data-mp="17lpcjhs59mgx"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10782" data-mp="1qcyjpkefzz5c">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10783" data-mp="1qfbc6rhttrwn">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10784" data-mp="3b22n1vtntljl">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-10785" data-mp="363hixlbaehpv" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10786" data-mp="3jv5u9mdpp25s">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10787" data-mp="3ftu6znib8t0r">
            <div data-id="element-10788" data-mp="2s3zux85tf8hk">
              <p className="text-sm font-medium text-gray-900" data-id="element-10789" data-mp="2s5kpuyw1ktfy">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10790" data-mp="2n7j2w2vhw9z7">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10791" data-mp="2w6mr1l9irfkk">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-10792" data-mp="gf8l9grae091" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10793" data-mp="10ez0sr7ozcng"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10794" data-mp="1eb45s9k0mar9">
            <div data-id="element-10795" data-mp="1j9nqk6mvhgi1">
              <p className="text-sm font-medium text-gray-900" data-id="element-10796" data-mp="1o7793iqlsffs">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10797" data-mp="1s8sqnhe31hwb">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10798" data-mp="3q03r5pixki9e">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-10799" data-mp="3u0nnwrzldjv0" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10800" data-mp="38pzuqppctfxu"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10801" data-mp="83pdu5lwhdn">
            <div data-id="element-10802" data-mp="3reenbhfbl20r">
              <p className="text-sm font-medium text-gray-900" data-id="element-10803" data-mp="3rey6f1gphwgd">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10804" data-mp="2kxaz0l1o6ema">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10805" data-mp="1dancz5uaagtk">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-10806" data-mp="199k10ufcx4xm" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10807" data-mp="1x0z62uyiq0ck"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10808" data-mp="20ywwg140yshj">
            <div data-id="element-10809" data-mp="6mq8e1q18h7t">
              <p className="text-sm font-medium text-gray-900" data-id="element-10810" data-mp="6n8c1jf0xxpr">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10811" data-mp="2i9scdddxc230">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10812" data-mp="2nb9bp4grfghh">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-10813" data-mp="29h8l4xzyozs7" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10814" data-mp="1qjxkb8yolyan"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10815" data-mp="3oxh5mu1pb4fo">
            <div data-id="element-10816" data-mp="11quhbrpc2l52">
              <p className="text-sm font-medium text-gray-900" data-id="element-10817" data-mp="11rco3i9mghr8">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10818" data-mp="wtb48v4dgeep">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10819" data-mp="nrledtq51nj1">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-10820" data-mp="1kgsom5lb4a7n" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10821" data-mp="244s1blzflrfp"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10822" data-mp="2bnj880uomfoe">
            <div data-id="element-10823" data-mp="2gmw0j14boeyd">
              <p className="text-sm font-medium text-gray-900" data-id="element-10824" data-mp="2gmchfh2xrkir">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10825" data-mp="2kozf3ymafznv">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10826" data-mp="2plpwcp9tx1av">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-10827" data-mp="2ulcar21e8nl9" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10828" data-mp="3deg4enem30fv"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-10829" data-mp="34ho345ywlmfl">
      <div data-id="element-10830" data-mp="34ifnekn1vxqk">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10831" data-mp="34hxtvvrrvxmc">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-10832" data-mp="2gr1xelcdge8r">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-10833" data-mp="2qppx440ho6nn">
        <div className="flex items-start gap-3" data-id="element-10834" data-mp="2qnn215khbnc2">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-10835" data-mp="2lpv702ojfl3n" />
          <div className="text-sm text-gray-700" data-id="element-10836" data-mp="5y7gap04aota">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10837" data-mp="1177i6bvv7yh">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-10838" data-mp="1iy12dyhikeqt">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10839" data-mp="1ew78qa5qkb05">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10840" data-mp="22m9wie2mausy">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10841" data-mp="22n35llp1twe9">
            <LockIcon size={18} className="text-gray-600" data-id="element-10842" data-mp="26ndcd0ko0qu6" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10843" data-mp="1sudx8zosoyn5">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-10844" data-mp="1xv1pfoorfliy">
            <div data-id="element-10845" data-mp="2lkvvnnekqbu8">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-10846" data-mp="2lkdv4ekvpbgi">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10847" data-mp="306tc1dwf0jr8">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10848" data-mp="2q83szhss8t9e">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10849" data-mp="2h9idxwmirwdd">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-10850" data-mp="2cia5b2t0kf19" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10851" data-mp="1xn3u81f29q12"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-10852" data-mp="d51pw6991fd7">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-10853" data-mp="n03hd8qzytiy" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10854" data-mp="32q6f318r4exk">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10855" data-mp="37pstvf48a38h">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10856" data-mp="2139ofpkq4jos">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10857" data-mp="1w5hvqtc98tjj" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-10858" data-mp="2a4zmvfd0wiw2">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-10859" data-mp="2a611tux5tpj2">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-10860" data-mp="2k1cgyechcyrc" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10861" data-mp="3hfkioksxmlyp" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-10862" data-mp="33i5qj8bao031">
        <div data-id="element-10863" data-mp="2os7uk674i63a">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10864" data-mp="2oshp873451fb">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10865" data-mp="2tr18u0u8i14i">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10866" data-mp="2jr230bp78ovc">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10867" data-mp="3r9e2q4e5npg8">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10868" data-mp="3n7sl65qoemzp">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-10869" data-mp="3i8fz3n7mpjy6" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10870" data-mp="3da5zn0evfx6r">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-10871" data-mp="9s4e5ogheny4">
            <table className="w-full text-left" data-id="element-10872" data-mp="9tfsgi7jze3h">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10873" data-mp="51e7m7wbkf1u">
                <tr data-id="element-10874" data-mp="4zkz0n25bkr7">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10875" data-mp="349ppj7n0cj">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10876" data-mp="xjjitnvqdhcj">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10877" data-mp="103pz8v1j709z">
                    <div className="flex items-center gap-1.5" data-id="element-10878" data-mp="152srtg0bre12">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-10879" data-mp="19zsx3opcocr4" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-10880" data-mp="1f1ib9d7vd6jf"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10881" data-mp="1j0r6ynmgzehm">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-10882" data-mp="1o03piw6b5zee">
                  <td className="px-6 py-4" data-id="element-10883" data-mp="1ny96xoiiejdh">
                    <div className="flex items-center gap-2" data-id="element-10884" data-mp="1ssdcc3c4mjcg">
                      <span className="text-sm font-medium text-gray-900" data-id="element-10885" data-mp="1xqf5jh39nz1n">
                        MYR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10886" data-mp="22qb2wxhgks88">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10887" data-mp="7k8yh1j0bz6p">
                    <div className="flex flex-col gap-1.5" data-id="element-10888" data-mp="2ns7lzkfb6px">
                      <div className="flex items-center gap-3" data-id="element-10889" data-mp="2lfi91cc5u2u">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-10890" data-mp="gfgkw66es18h">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-10891" data-mp="bm40674uoel0" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-10892" data-mp="ldqguizyj5v5">
                          <span className="text-sm text-gray-900" data-id="element-10893" data-mp="lfuhms2k7or7">
                            {BRAND.name}
                          </span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-10894" data-mp="vbefhk0ooxil">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-10895" data-mp="2x5dn9velkwqj">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-10896" data-mp="2mzvv6u8d8n3f">
                            <ClockIcon size={12} data-id="element-10897" data-mp="2dacark508l6g" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-10898" data-mp="2i72s0asjpmtg">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-10899" data-mp="28ag7jn44sxry">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10900" data-mp="1zbmg0b5nqxl9">
                    <span className="text-sm text-gray-600" data-id="element-10901" data-mp="3oog20ik7jv7t">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-10902" data-mp="3to2hkynidnjs">
                    <div className="flex items-center justify-end gap-2" data-id="element-10903" data-mp="3tlgbl3g9ng3o">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10904" data-mp="1tl1d63ipptc5">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-10905" data-mp="rw7mzagbzcb1">
                        <MoreHorizontalIcon size={18} data-id="element-10906" data-mp="2ykzmtm0ism8h" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-10907" data-mp="37kwabkb6bf1y">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-10908" data-mp="3cjz6sgd38cyb">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10909" data-mp="2jwd5jerc1kem">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10910" data-mp="2ou4zeebjfglg">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10911" data-mp="2tld7n714svx1">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-10912" data-mp="2tn7uiqvyqy3r" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10913" data-mp="214sptdcat6im">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-10914" data-mp="34dn3a6apwaz3">
            <div className="flex items-start gap-4" data-id="element-10915" data-mp="2zhojkw0c3h50">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-10916" data-mp="2zeskv5gnwofi">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-10917" data-mp="3d7ibjn74yhiy" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10918" data-mp="2giaogaqvc0cx"></div>
              </label>

              <div className="flex-1" data-id="element-10919" data-mp="9rkvixmamfcp">
                <p className="text-sm font-medium text-gray-900" data-id="element-10920" data-mp="9qjmeyou82xm">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-10921" data-mp="4uuhefu1tttx">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-10922" data-mp="3bayc2hmzark">
                    <div className="relative max-w-xs" data-id="element-10923" data-mp="3ccntbzefzst">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10924" data-mp="8ae4jq9crn14">
                        <span className="text-gray-500 text-sm" data-id="element-10925" data-mp="w08he7to9bld">MYR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-10926" data-mp="10ytgg3x6tp88" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-10927" data-mp="3fuab08irzatg">
                        <InfoIcon size={14} data-id="element-10928" data-mp="3b0fs37azuzvf" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10929" data-mp="2m75qgz2f5bp">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10930" data-mp="2jt9w5wk9p33">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#E2231A] hover:bg-[#EB4A42] text-white'}`} data-id="element-10931" data-mp="3psq1qadw4i2f">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10932" data-mp="osrpi8s0ohza">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10933" data-mp="tu7egbxbf8my">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10934" data-mp="xu80ahmqum41">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-10935" data-mp="12m973o157kmo" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10936" data-mp="17kkjo6ito98l">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-10937" data-mp="1hjjjczlm0ujg">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-10938" data-mp="1hkl2zsgoypcs">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10939" data-mp="1lkbslsuhezfq" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-10940" data-mp="3jczkiqfbprso">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-10941" data-mp="3dqtj84hea4sd">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" data-id="element-10942" data-mp="33y5jb0v4bkpf" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10943" data-mp="3np0via0qnyo1">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10944" data-mp="27ar9hmjyym0d">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[#E2231A] hover:bg-[#EB4A42] text-white rounded-md transition-colors" data-id="element-10945" data-mp="23stra39zjglm">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-10946" data-mp="1l0932z58dzo5">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10947" data-mp="1kzhiejd69mcn">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10948" data-mp="1g1670478b3sb">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10949" data-mp="1g2r2px5a4srr">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10950" data-mp="1uqc1xfz1a7ue">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10951" data-mp="1urvhf4cv8ev4">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10952" data-mp="1pxrd6suzikxq">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-10953" data-mp="12981s4gn1s1f">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-10954" data-mp="2xcekrbkfhove">
      <div data-id="element-10955" data-mp="32br3bk49o9s6">
        <p className="text-sm text-gray-900 font-medium" data-id="element-10956" data-mp="32b7o4b603zhu">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-10957" data-mp="372pp2z80y02t">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-10958" data-mp="373iyk7ydb3on">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-10959" data-mp="2je6f8f9btf8m">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-10960" data-mp="2jdy68ygbqu2p">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-10961" data-mp="2ndyoyvaghrfk">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10962" data-mp="2se38yvakp4aj">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10963" data-mp="2sbgtm9hfxkhr">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-10964" data-mp="2qluf22zgs33" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10965" data-mp="hrj6lcxtftv2">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-10966" data-mp="81pwkgdbnmsx">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10967" data-mp="80o8nbb7j5ts">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10968" data-mp="3zcsrxi4xyys">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10969" data-mp="3v78g6vzb3ccw">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10970" data-mp="3q72urxjmqm39">
                  <div className="flex items-center justify-between" data-id="element-10971" data-mp="3l7pzqpilu8pl">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10972" data-mp="3la2qns6cbtes">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10973" data-mp="1jhelhda4yvwd">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10974" data-mp="1mjsrizx4mr48">
                  <div className="flex items-center justify-between" data-id="element-10975" data-mp="1rhki9qm1c76u">
                    <span className="text-xs text-gray-600" data-id="element-10976" data-mp="1rgsxzbxw1vvv">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10977" data-mp="1dh1faxwffrbj">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10978" data-mp="13s9oit33rd27">
                    <span className="text-xs text-gray-600" data-id="element-10979" data-mp="13q6rhp3j8jnz">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10980" data-mp="18k182jnuciiu">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10981" data-mp="ys59z8lr8sem">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10982" data-mp="kt5kjyqf49he">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10983" data-mp="psi7ejhahgjz">
                      <p className="text-xs text-gray-600" data-id="element-10984" data-mp="666vgaf19r5u">
                        <span className="font-medium" data-id="element-10985" data-mp="66p1fyrhzjqy">MYR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10986" data-mp="196ntv2jcgcm">
                        <span className="font-medium" data-id="element-10987" data-mp="f7dcqlpib605">MYR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10988" data-mp="p3q8rq8ixvy2">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-10989" data-mp="k56hdab5vryb">
                  <div className="flex items-center justify-between" data-id="element-10990" data-mp="yvdurbmbaks5">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10991" data-mp="tw156j422zlv">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10992" data-mp="17wu4ri97skvu">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10993" data-mp="l4h4gz8fxzl">
                  <div className="flex items-center justify-between" data-id="element-10994" data-mp="5huxl5ey8vlj">
                    <span className="text-xs text-gray-600" data-id="element-10995" data-mp="5g0dftbi57ii">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10996" data-mp="9iol3xohc8dw">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10997" data-mp="jakmbhlv4emc">
                    <span className="text-xs text-gray-600" data-id="element-10998" data-mp="o8m29tnzrxtl">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10999" data-mp="oap2f6iuz7c1">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11000" data-mp="x8960m471fpt">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-11001" data-mp="2uztwihu36n8l">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-11002" data-mp="2z1oyzpnr76ny">
                      <p className="text-xs text-gray-600" data-id="element-11003" data-mp="2rho278vt0gjp">
                        <span className="font-medium" data-id="element-11004" data-mp="2rj8xx1tuu5j5">MYR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-11005" data-mp="2mouwaholn261">
                        <span className="font-medium" data-id="element-11006" data-mp="2hse6lj1r4fqu">MYR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11007" data-mp="3ae5z04pif6mt">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-11008" data-mp="35hyt2bx45v4v">
                  <div className="flex items-center justify-between" data-id="element-11009" data-mp="31e9aq47189sa">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11010" data-mp="2whaf1h7pzurz">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11011" data-mp="1q1q9ci377uig">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-11012" data-mp="3p56wqxf4u9p6">
                  <div className="flex items-center justify-between" data-id="element-11013" data-mp="3p5yep5ft48wz">
                    <span className="text-xs text-gray-600" data-id="element-11014" data-mp="3b5xdfovmqzgz">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11015" data-mp="3g4h4g3p2z1g7">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11016" data-mp="7p74b14r46b2">
                    <p className="text-xs text-gray-500" data-id="element-11017" data-mp="co1n432pxuv6">
                      + MYR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-11018" data-mp="qnjgkvqylwav">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-11019" data-mp="qn1912yujvnn">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-11020" data-mp="vf1f7d5ifkso">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11021" data-mp="2g3dk85zqwvcy">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-11022" data-mp="2b4iv6mb6qake">
                  <div className="flex items-center justify-between" data-id="element-11023" data-mp="2oxi76ebrdmn7">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11024" data-mp="2oytbcf948weu">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11025" data-mp="1x511ptmy294p">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11026" data-mp="268269pw5vs69">
                  <div className="flex items-center justify-between mb-2" data-id="element-11027" data-mp="217e6aepyg0zw">
                    <span className="text-xs text-gray-600" data-id="element-11028" data-mp="219ij5m5u0acb">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11029" data-mp="3hkcpb4l3s9xi">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11030" data-mp="32d71n9yg3hfn">
                    <p className="text-xs text-gray-500" data-id="element-11031" data-mp="32dfhnapv72v2">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11032" data-mp="37c9y460d0fc0">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-11033" data-mp="37ecztnavjwwk">
                  <div className="flex items-center justify-between" data-id="element-11034" data-mp="3la8bp96uw7qg">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11035" data-mp="3gbx2n0ody19a">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11036" data-mp="3v12xkcg6x5bd">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11037" data-mp="3vjlh8a9v41c">
                  <div className="flex items-center justify-between mb-2" data-id="element-11038" data-mp="3wl3jwppgqsk">
                    <span className="text-xs text-gray-600" data-id="element-11039" data-mp="mjc3k4dyhrcn">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11040" data-mp="mi10k6sc4nml">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11041" data-mp="didv99whixo9">
                    <p className="text-xs text-gray-500" data-id="element-11042" data-mp="ii0bls7m0u1a">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11043" data-mp="8l49q5c7v65p">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-11044" data-mp="3qylkl908rn4e">
                  <div className="flex items-center justify-between" data-id="element-11045" data-mp="3uzdw68wl643u">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11046" data-mp="3uxkocqa8ldu9">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11047" data-mp="3lyphrd3a61qf">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11048" data-mp="1mqz907tzqcam">
                  <div className="flex items-center justify-between mb-2" data-id="element-11049" data-mp="1mpxntaj9g9f6">
                    <span className="text-xs text-gray-600" data-id="element-11050" data-mp="1iqykfud6gkpu">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11051" data-mp="1ds56k0tzarp5">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11052" data-mp="2afigil57ul1n">
                    <p className="text-xs text-gray-500" data-id="element-11053" data-mp="26hce1pbeurmj">
                      + MYR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11054" data-mp="1wjy0bxgvggxx">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-11055" data-mp="q3ch8n7v3i3i">
                  <div className="flex items-center justify-between" data-id="element-11056" data-mp="l1ww6v5opbl4">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11057" data-mp="az9q3py46utj">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-11058" data-mp="fym9g0ps1jrd">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-11059" data-mp="ppgzzha235im">
                  <div className="flex items-center justify-between mb-2" data-id="element-11060" data-mp="tq0wcimt253p">
                    <span className="text-xs text-gray-600" data-id="element-11061" data-mp="yoc84ywnupok">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-11062" data-mp="yqf5k4057l3b">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-11063" data-mp="18mrz91votyy2">
                    <p className="text-xs text-gray-500" data-id="element-11064" data-mp="35es3o3dv3192">
                      + MYR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-11065" data-mp="14ot9467snzus">
            <div className="flex items-start gap-2" data-id="element-11066" data-mp="zqrp9j2jnwi9">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-11067" data-mp="zpzxkjc2iix9" />
              <div className="text-sm text-gray-700" data-id="element-11068" data-mp="1dnwq94nei56n">
                <p className="font-medium text-gray-900 mb-1" data-id="element-11069" data-mp="18shfilkowpeg">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-11070" data-mp="l0tziyoyuyqy">
                  <li data-id="element-11071" data-mp="h0a0fpku1l26">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-11072" data-mp="uszlnrsjely8">
                    Lower rates apply to CIMB-issued cards (on-us transactions)
                  </li>
                  <li data-id="element-11073" data-mp="pyvg9cyx6lz9">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-11074" data-mp="28ze93sqk1cpm">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-11075" data-mp="28xtddzsi7nq6">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-11076" data-mp="2dr4jfyphs0h1" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-11077" data-mp="200j068w4loim">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-11078" data-mp="2snn0i0k9xeo2">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11079" data-mp="2sliwzjqaehob">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11080" data-mp="2wo5spvq2wmqs">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-11081" data-mp="2ioe7b9x8g42r">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-11082" data-mp="17a0ncg5lo8s0">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-11083" data-mp="17993u3pa21i3" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-11084" data-mp="s7ipl56fkyeb" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-11085" data-mp="jep954f8zsyv" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11086" data-mp="9g9h8z8ecfob">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11087" data-mp="9e53zqoly4bd">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-11088" data-mp="4g3jd1bj9wxs">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-11089" data-mp="3rmnran3jo5hf">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-11090" data-mp="3rnyuolt2vb80" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-11091" data-mp="1pw2kpgmk69fc" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-11092" data-mp="22clgegn5q3o0" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11093" data-mp="2c1vce7l4g6he">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-11094" data-mp="1eg7rojy13clp">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-11095" data-mp="1je962rkienqu">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-11096" data-mp="1ocss0rz3rzj7">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11097" data-mp="1sf7ahayv0x9y">
                <tr data-id="element-11098" data-mp="1sd2zk92jmy06">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-11099" data-mp="2zsr8hgwm1nug">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-11100" data-mp="33tkwsbdyt4nv">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-11101" data-mp="3pw60qh2z4270">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11102" data-mp="3hd45iz73k5e">
                <tr data-id="element-11103" data-mp="3uv93tvze7ycc">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11104" data-mp="3uty36518v6pg">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11105" data-mp="cj2yoxodiz7e">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-11106" data-mp="mew6y4v286jd">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-11107" data-mp="2o7c3j426ckwe">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11108" data-mp="2o8n3essi18i8">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11109" data-mp="ypneyfi49tq8">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-11110" data-mp="13n6vxn678klv">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-11111" data-mp="twc84edb1cyb">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-11112" data-mp="fz5roqg1zqc9">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-11113" data-mp="k1an9uq2ax1j">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-11114" data-mp="632qpnwxtxqw">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-11115" data-mp="375rr33cvywja">
            <div className="flex items-start gap-2" data-id="element-11116" data-mp="3c4kyce1kxrb0">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-11117" data-mp="3c54g9ur8cfp1" />
              <div className="text-sm text-gray-700" data-id="element-11118" data-mp="3h1uusdndz38c">
                <p className="font-medium text-gray-900 mb-1" data-id="element-11119" data-mp="3lvfty90pu394">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-11120" data-mp="3qu0opsx77uq6">
                  <li data-id="element-11121" data-mp="3uv2nk8n7e4rm">
                    <span className="font-medium" data-id="element-11122" data-mp="3uujdfe3soko5">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-11123" data-mp="3moslhdx6qqa">
                    Example: MYR 100 Visa on-us = (MYR 100 × 1.10%) + MYR 0.50 =
                    MYR 1.60
                  </li>
                  <li data-id="element-11124" data-mp="25gxw7wzk7rbu">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-11125" data-mp="22w84hrym6i9c">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-11126" data-mp="22xt3btryond0">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-11127" data-mp="1y3otn2rba188" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-11128" data-mp="1t3smiunbhtog">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-11129" data-mp="1o84y4hgnt4k0">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11130" data-mp="2lv4097q4f4pa">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-11131" data-mp="2gwsogrg9mk4f">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11132" data-mp="26y366xkgixnn">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-11133" data-mp="10hr80whmxfs2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11134" data-mp="10evah99p8t45">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-11135" data-mp="3jntvola80dbs">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11136" data-mp="39pw6nvkjeci4">
                MYR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-11137" data-mp="3eqtrkfwxlknb">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-11138" data-mp="300cqixo7kwqy">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-11139" data-mp="34qt4civxykdu" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-11140" data-mp="2qzy1bn84ixi6">
            <div data-id="element-11141" data-mp="2vxzlycl774vr">
              <p className="text-sm font-medium text-gray-900" data-id="element-11142" data-mp="2vzkfbyvs0hs1">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-11143" data-mp="2h20en45wx8ro">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-11144" data-mp="22e9tk3x0qqpb">
              <p className="text-lg font-bold text-gray-900" data-id="element-11145" data-mp="22fb6kdxlhn9o">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-11146" data-mp="2ccze64gaup99">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-11147" data-mp="2lcm3vszo26mh">
            <p className="text-xs text-gray-600" data-id="element-11148" data-mp="2hcbzulvfpqa9">
              <span className="font-medium" data-id="element-11149" data-mp="2hbk337q3pmie">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-11150" data-mp="35kwzn4zdd1d5">
        <div data-id="element-11151" data-mp="31l4w56ou1len">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11152" data-mp="31klelr33gz15">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-11153" data-mp="2wncxltton11h">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11154" data-mp="2rptiyst2om90">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11155" data-mp="uuf8o9310slt">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11156" data-mp="pxf2zza39rv8">
              <SearchIcon size={18} className="text-gray-600" data-id="element-11157" data-mp="2kbasu3bll6d0" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-11158" data-mp="2p4lr3g2cgetb">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-11159" data-mp="2ae66a2dub8vo">
              <div data-id="element-11160" data-mp="2ac1u6x5sf3kb">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11161" data-mp="2fczk5vx1h1wd">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-11162" data-mp="333v6idiq74w8" />
              </div>
              <div data-id="element-11163" data-mp="1mpvc1i2ng7gd">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11164" data-mp="1mm7hqgpdrh0l">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-11165" data-mp="3vy1fzqo8f1qd">
                  <option value="all" data-id="element-11166" data-mp="3d8lccuh92t5q">All Time</option>
                  <option value="24h" data-id="element-11167" data-mp="3qyf1ldwo3ffz">Last 24 Hours</option>
                  <option value="7days" data-id="element-11168" data-mp="3qzzyha6gfah0">Last 7 Days</option>
                  <option value="30days" data-id="element-11169" data-mp="3m6na915ot5os">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-11170" data-mp="2ygb49vu05j0u">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11171" data-mp="2yfrlye0fwsma">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-11172" data-mp="3jbbdwnlva1g3">
                  <option value="all" data-id="element-11173" data-mp="63gsj7wp61j1">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-11174" data-mp="b2kzdrw5fn67">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-11175" data-mp="jwg3qxpg59ks">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11176" data-mp="juv814rebklc">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-11177" data-mp="owuc6guymljc">
                  <option value="all" data-id="element-11178" data-mp="pyjen4w06wsv">All Sections</option>
                  <option value="Payments" data-id="element-11179" data-mp="q02u4t9u53tl">Payments</option>
                  <option value="Customers" data-id="element-11180" data-mp="1mosdyp50w6i7">Customers</option>
                  <option value="Invoices" data-id="element-11181" data-mp="1hpfr44e5izfm">Invoices</option>
                  <option value="Bills" data-id="element-11182" data-mp="1hpzalpjg9vvr">Bills</option>
                  <option value="Refunds" data-id="element-11183" data-mp="1cyh9n1hffvas">Refunds</option>
                  <option value="Users" data-id="element-11184" data-mp="1cwwfve2xsgdz">Users</option>
                  <option value="Settings" data-id="element-11185" data-mp="18xxchxwusron">Settings</option>
                  <option value="Security" data-id="element-11186" data-mp="18vb5bzdvke6y">Security</option>
                </select>
              </div>
              <div data-id="element-11187" data-mp="25guzoewiuczb">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-11188" data-mp="20ojx14aecd32">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-11189" data-mp="3w10adxa3h70g">
                  <option value="25" data-id="element-11190" data-mp="3dcvaj0wk666x">25</option>
                  <option value="50" data-id="element-11191" data-mp="3d9h9px3jm51l">50</option>
                  <option value="100" data-id="element-11192" data-mp="38bph10v2qewc">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-11193" data-mp="33efjexxj6wxf">
              <p className="text-sm text-gray-600" data-id="element-11194" data-mp="2zckihujiilk6">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11195" data-mp="xkz65t46y0kb">
          <div className="overflow-x-auto" data-id="element-11196" data-mp="xiv3tflxx9m5">
            <table className="w-full" data-id="element-11197" data-mp="2fh9j44k68iym">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11198" data-mp="2fg87nyz8tugd">
                <tr data-id="element-11199" data-mp="2kg4a3tsaldtt">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11200" data-mp="2kfkp247ci9bk">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11201" data-mp="2t8o2ichzqm67">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11202" data-mp="21ijhqcqbjx4g">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11203" data-mp="25itrm0h8f7ol">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11204" data-mp="3gy9zquesyrbk">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11205" data-mp="1lw5aj8zt65on">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11206" data-mp="1vu2xlt5xlwfo">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-11207" data-mp="1quyn8z79tsnr">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-11208" data-mp="133ktqpxdvr17">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-11209" data-mp="183pamh27enry">
                      <div className="flex items-center gap-2" data-id="element-11210" data-mp="182xft8gfku2q">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-11211" data-mp="kduxrzhvwz7p">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-11212" data-mp="2i2qesq2d84i0">
                          <p className="text-sm font-medium text-gray-900" data-id="element-11213" data-mp="2i3jdr4v31ppl">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-11214" data-mp="2m28zplu6sfku">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11215" data-mp="30t85kp3n0q0q">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11216" data-mp="30sg7n7mkig7a">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-11217" data-mp="1pg5olsxmlyg1">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-11218" data-mp="29mj060rmx8me">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-11219" data-mp="24var55u7vp9r">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-11220" data-mp="2sm6osjlcteox">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11221" data-mp="2nmbvk9e3wd0p">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-11222" data-mp="139384batdlqc">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11223" data-mp="3jsndqdyqootn">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-11224" data-mp="2b34fakuf71q">
        <div data-id="element-11225" data-mp="h0iueocjv8gd">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-11226" data-mp="h0922u418p7i">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-11227" data-mp="c15w97g8aeyh">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-11228" data-mp="l23zhniueig0">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-11229" data-mp="l014sq6qw14y">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-11230" data-mp="14ofe64chglxu">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-11231" data-mp="19lnyobl3t229" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-11232" data-mp="zoj901s1uogp">
            <table className="w-full" data-id="element-11233" data-mp="znhutofqllur">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-11234" data-mp="zqnjj2kgelpy">
                <tr data-id="element-11235" data-mp="lw3ddm1h9spt">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11236" data-mp="lvl8k10r266a">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11237" data-mp="c0ta6uzcffag">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11238" data-mp="2y0k2v1wd9m7">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11239" data-mp="7zg2sgglr41f">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11240" data-mp="ih35csh5lpza">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11241" data-mp="9gn3tojz0sx2">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-11242" data-mp="3vueyb7opnyt3">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-11243" data-mp="3qwwq5jiipnm5">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-11244" data-mp="3hvf99yxadhwe">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-11245" data-mp="3cx3x3hjiqvb0">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-11246" data-mp="1xdmkexew7vzz">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-11247" data-mp="21garp0nykuuu">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-11248" data-mp="26c88t971q58o">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-11249" data-mp="1in6z7imh8o1t">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-11250" data-mp="1nf6swtbyekpy">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11251" data-mp="9o34qtanu6tu">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11252" data-mp="9nju7xncakpu">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11253" data-mp="3rswnr1ipcvtu">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-11254" data-mp="ltv05n7uhhi">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11255" data-mp="3muv4afhd6uhu">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11256" data-mp="3mw6649r91s6b">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-11257" data-mp="391nach5t9mwz">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-11258" data-mp="2z5smn7mqd1nc">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-11259" data-mp="344n3w551ui5c">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-11260" data-mp="2kjd5q8b71tcm">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-11261" data-mp="3pald9k60ns0w">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-11262" data-mp="3u8myobqx03fj">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-11263" data-mp="32lsohlt400r">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-11264" data-mp="3092xi9t4ld5">
          <div className="lg:hidden mb-4" data-id="element-11265" data-mp="7talc098331h">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-11266" data-mp="7udbg5yq11gc">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-11267" data-mp="lsaad90ndjy6">
                    <Icon size={16} data-id="element-11268" data-mp="2slds4d9b7kxi" />
                    <span data-id="element-11269" data-mp="19oorjr1ljsnl">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-11270" data-mp="14wn89l60h1o2">
            <div className="space-y-6" data-id="element-11271" data-mp="10vuwol9o2kom">
              {categories.map(category => <div key={category.id} data-id="element-11272" data-mp="10th2sfqq3ein">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-11273" data-mp="vxjnmcr74e7g">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-11274" data-mp="1olfs43n0baes">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-11275" data-mp="87pdi2y9h3qw">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-11276" data-mp="88h9hevrt3hp">
                              <Icon size={16} data-id="element-11277" data-mp="1i4memobmw9be" />
                              <span data-id="element-11278" data-mp="1i5nnqn93alqh">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-11279" data-mp="2etbwien9h1xd">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}